import { useTranslation } from '../../../shared/i18n';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import moment from 'moment';

/**
 * InvoicePrint — 账单打印 / 导出 PDF
 *
 * 实现方式：把纯净的发票文档 (InvoicePrintDocument) 用 renderToStaticMarkup
 * 渲染成完整 HTML，写入一个隐藏 iframe（独立 document，自带内联样式），
 * 再调用 iframe.contentWindow.print()。
 *
 * 相比 window.open（会被弹窗拦截器拦截）和 @media print 隐藏整个 App
 * （antd / 布局样式容易串进打印结果），iframe 隔离文档最稳。
 */

// ---------- helpers ----------

const fmtMoney = (value) => {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return '0.00';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (value, withTime = false) => {
    if (!value) return '—';
    const m = moment(value);
    if (!m.isValid()) return '—';
    return m.format(withTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD');
};

const STATUS_MAP = {
    draft: { stamp: 'DRAFT', zh: '草稿', tone: 'muted' },
    unpaid: { stamp: 'UNPAID', zh: '未支付', tone: 'due' },
    paid: { stamp: 'PAID', zh: '已支付', tone: 'ok' },
    cancelled: { stamp: 'CANCELLED', zh: '已取消', tone: 'muted' },
    refunded: { stamp: 'REFUNDED', zh: '已退款', tone: 'muted' },
    void: { stamp: 'VOID', zh: '已作废', tone: 'muted' },
};

const PAYMENT_METHOD_LABELS = {
    balance: '账户余额 Balance',
    saved_card: '银行卡 Card',
    card: '银行卡 Card',
    stripe: '银行卡 Card (Stripe)',
    google_pay: 'Google Pay',
    alipay: '支付宝 Alipay',
    wechat_pay: '微信支付 WeChat Pay',
    paypal: 'PayPal',
    manual: '人工标记 Manual',
    admin: '人工标记 Manual',
};

const INVOICE_TYPE_LABELS = {
    subscription: '订阅 Subscription',
    renewal: '续费 Renewal',
    topup: '余额充值 Top-up',
    upgrade: '升级 Upgrade',
    usage: '用量 Usage',
    manual: '手工 Manual',
};

// 打印文档专用样式：黑白友好、A4 排版，全部内联进 iframe。
const PRINT_CSS = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: A4; margin: 16mm 14mm; }
    html, body { background: #fff; }
    body {
        font-family: 'Helvetica Neue', Arial, 'PingFang SC', 'Hiragino Sans GB',
            'Microsoft YaHei', 'Noto Sans CJK SC', sans-serif;
        color: #1a1a1a;
        font-size: 12px;
        line-height: 1.55;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .inv-page { max-width: 720px; margin: 0 auto; padding: 24px 8px; }

    .inv-topbar {
        display: flex; justify-content: space-between; align-items: flex-start;
        padding-bottom: 14px; border-bottom: 3px solid #1a1a1a;
    }
    .inv-brand { font-size: 22px; font-weight: 700; letter-spacing: 0.5px; }
    .inv-brand small { display: block; font-size: 10px; font-weight: 400; color: #666; letter-spacing: 2px; text-transform: uppercase; margin-top: 2px; }
    .inv-doc-title { text-align: right; }
    .inv-doc-title h1 { font-size: 26px; font-weight: 700; letter-spacing: 4px; }
    .inv-doc-title .inv-no { font-size: 12px; color: #444; margin-top: 4px; font-family: 'SF Mono', Consolas, Menlo, monospace; }

    .inv-stamp-row { position: relative; }
    .inv-stamp {
        position: absolute; top: 14px; right: 0;
        border: 3px solid #1a1a1a; border-radius: 6px;
        padding: 3px 14px; font-size: 15px; font-weight: 800; letter-spacing: 3px;
        transform: rotate(-6deg); opacity: 0.85; text-transform: uppercase;
    }
    .inv-stamp.tone-ok { border-color: #2f7d32; color: #2f7d32; }
    .inv-stamp.tone-due { border-color: #b02a2a; color: #b02a2a; }
    .inv-stamp.tone-muted { border-color: #777; color: #777; }

    .inv-meta {
        display: flex; justify-content: space-between; gap: 24px;
        margin-top: 18px;
    }
    .inv-meta-col { min-width: 0; }
    .inv-meta-col.right { text-align: right; }
    .inv-label {
        font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px;
        text-transform: uppercase; color: #888; margin-bottom: 3px;
    }
    .inv-meta-col .val { font-size: 12px; color: #1a1a1a; }
    .inv-meta-col .val.strong { font-weight: 600; font-size: 13px; }
    .inv-meta-col + .inv-meta-col { margin-left: 0; }
    .inv-meta-block { margin-bottom: 10px; }

    table.inv-items { width: 100%; border-collapse: collapse; margin-top: 22px; }
    table.inv-items th {
        text-align: left; font-size: 9.5px; letter-spacing: 1.2px; text-transform: uppercase;
        color: #555; padding: 7px 8px; border-top: 2px solid #1a1a1a; border-bottom: 1px solid #1a1a1a;
    }
    table.inv-items td { padding: 8px; border-bottom: 1px solid #ddd; vertical-align: top; font-size: 12px; }
    table.inv-items .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    table.inv-items .qty { text-align: center; white-space: nowrap; }
    table.inv-items td.empty { text-align: center; color: #888; padding: 18px 8px; }
    table.inv-items tr { page-break-inside: avoid; }

    .inv-totals { display: flex; justify-content: flex-end; margin-top: 10px; page-break-inside: avoid; }
    .inv-totals table { border-collapse: collapse; min-width: 280px; }
    .inv-totals td { padding: 4px 8px; font-size: 12px; }
    .inv-totals td.k { color: #555; text-align: right; }
    .inv-totals td.v { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .inv-totals tr.grand td {
        border-top: 2px solid #1a1a1a; padding-top: 8px;
        font-size: 15px; font-weight: 700;
    }

    .inv-payinfo { margin-top: 26px; padding-top: 12px; border-top: 1px solid #ccc; page-break-inside: avoid; }
    .inv-payinfo .row { display: flex; gap: 40px; }
    .inv-notes { margin-top: 14px; page-break-inside: avoid; }
    .inv-notes .val { white-space: pre-wrap; color: #333; }

    .inv-footer {
        margin-top: 36px; padding-top: 10px; border-top: 1px solid #ccc;
        font-size: 10px; color: #888; text-align: center; line-height: 1.7;
        page-break-inside: avoid;
    }
`;

// ---------- printable document (pure markup) ----------

const InvoicePrintDocument = ({ invoice }) => {
    const { t } = useTranslation();
    if (!invoice) return null;

    const currency = invoice.currency || 'USD';
    const status = STATUS_MAP[invoice.status] || { stamp: String(invoice.status || '').toUpperCase(), zh: invoice.status || '—', tone: 'muted' };
    const items = Array.isArray(invoice.items) ? invoice.items : [];
    const billName = invoice.username || invoice.user?.username || '—';
    const billEmail = invoice.email || invoice.user?.email || '';
    const discount = parseFloat(invoice.discount_amount) || 0;
    const tax = parseFloat(invoice.tax_amount) || 0;
    const paymentMethod = invoice.payment_method
        ? (PAYMENT_METHOD_LABELS[invoice.payment_method] || invoice.payment_method)
        : null;
    const invoiceType = INVOICE_TYPE_LABELS[invoice.invoice_type] || invoice.invoice_type || '—';

    return (
        <div className="inv-page">
            {/* Header */}
            <div className="inv-topbar">
                <div className="inv-brand">
                    Yeap Cloud
                    <small>Cloud Services Platform</small>
                </div>
                <div className="inv-doc-title">
                    <h1>{t('Invoice')}</h1>
                    <div className="inv-no">{invoice.invoice_number || `#${invoice.id ?? ''}`}</div>
                </div>
            </div>

            {/* Status stamp */}
            <div className="inv-stamp-row">
                <div className={`inv-stamp tone-${status.tone}`}>{status.stamp}</div>
            </div>

            {/* Meta: bill-to + dates */}
            <div className="inv-meta">
                <div className="inv-meta-col">
                    <div className="inv-meta-block">
                        <div className="inv-label">{t('Bill to')}</div>
                        <div className="val strong">{billName}</div>
                        {billEmail ? <div className="val">{billEmail}</div> : null}
                    </div>
                    <div className="inv-meta-block">
                        <div className="inv-label">{t('Invoice type')}</div>
                        <div className="val">{t(invoiceType)}</div>
                    </div>
                </div>
                <div className="inv-meta-col right">
                    <div className="inv-meta-block">
                        <div className="inv-label">{t('Issue date')}</div>
                        <div className="val">{fmtDate(invoice.issue_date || invoice.created_at)}</div>
                    </div>
                    <div className="inv-meta-block">
                        <div className="inv-label">{t('Due date')}</div>
                        <div className="val">{fmtDate(invoice.due_date)}</div>
                    </div>
                    {invoice.paid_at ? (
                        <div className="inv-meta-block">
                            <div className="inv-label">{t('Paid At')}</div>
                            <div className="val">{fmtDate(invoice.paid_at, true)}</div>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Items */}
            <table className="inv-items">
                <thead>
                    <tr>
                        <th style={{ width: '48%' }}>{t('Item description')}</th>
                        <th className="qty">{t('Qty')}</th>
                        <th className="num">{t('Unit price')}</th>
                        <th className="num">{t('Amount')}</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr>
                            <td className="empty" colSpan={4}>
                                {invoice.description || t('— No line items —')}
                            </td>
                        </tr>
                    ) : (
                        items.map((item, idx) => (
                            <tr key={item.id ?? idx}>
                                <td>{item.description || '—'}</td>
                                <td className="qty">{item.quantity ?? 1}</td>
                                <td className="num">{currency} {fmtMoney(item.unit_price)}</td>
                                <td className="num">{currency} {fmtMoney(item.amount)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {/* Totals */}
            <div className="inv-totals">
                <table>
                    <tbody>
                        <tr>
                            <td className="k">{t('Subtotal')}</td>
                            <td className="v">{currency} {fmtMoney(invoice.subtotal ?? invoice.total_amount)}</td>
                        </tr>
                        {discount > 0 ? (
                            <tr>
                                <td className="k">{t('Discount')}</td>
                                <td className="v">− {currency} {fmtMoney(discount)}</td>
                            </tr>
                        ) : null}
                        {tax > 0 ? (
                            <tr>
                                <td className="k">
                                    {t('Tax{{rate}}', { rate: invoice.tax_rate ? ` (${invoice.tax_rate}%)` : '' })}
                                </td>
                                <td className="v">{currency} {fmtMoney(tax)}</td>
                            </tr>
                        ) : null}
                        <tr className="grand">
                            <td className="k">{t('Total')}</td>
                            <td className="v">{currency} {fmtMoney(invoice.total_amount)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Payment info */}
            <div className="inv-payinfo">
                <div className="row">
                    <div>
                        <div className="inv-label">{t('Payment Status')}</div>
                        <div className="val">{t(status.zh)} {status.stamp}</div>
                    </div>
                    {paymentMethod ? (
                        <div>
                            <div className="inv-label">{t('Payment Method')}</div>
                            <div className="val">{t(paymentMethod)}</div>
                        </div>
                    ) : null}
                    {invoice.transaction_id ? (
                        <div>
                            <div className="inv-label">{t('Transaction ID')}</div>
                            <div className="val">{invoice.transaction_id}</div>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Notes */}
            {invoice.notes ? (
                <div className="inv-notes">
                    <div className="inv-label">{t('Notes')}</div>
                    <div className="val">{invoice.notes}</div>
                </div>
            ) : null}

            {/* Footer */}
            <div className="inv-footer">
                {t('This invoice was generated electronically by Yeap Cloud and is valid without signature or seal.')}
                <br />
                {t('For any questions about this invoice, please contact support.')}
                &nbsp;&nbsp;·&nbsp;&nbsp;{t('Printed at {{time}}', { time: moment().format('YYYY-MM-DD HH:mm') })}
            </div>
        </div>
    );
};

// ---------- print trigger ----------

const escapeHtml = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));

/**
 * 打开浏览器打印对话框，仅渲染发票文档本身。
 * 用户在打印对话框中选择“另存为 PDF”即可导出 PDF。
 * @param {object} invoice - 完整的发票数据（含 items / username / email）
 */
export const printInvoice = (invoice) => {
    if (!invoice) return;

    // 文档 title 决定“另存为 PDF”时的默认文件名
    const title = `Invoice-${invoice.invoice_number || invoice.id || ''}`;
    const body = renderToStaticMarkup(<InvoicePrintDocument invoice={invoice} />);
    const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${PRINT_CSS}</style>
</head>
<body>${body}</body>
</html>`;

    // 隐藏 iframe：独立 document，不受 App/antd 样式影响，也不会被弹窗拦截器拦截
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);

    let removed = false;
    const cleanup = () => {
        if (removed) return;
        removed = true;
        // 稍等再移除，避免部分浏览器打印进程还在引用 iframe 文档
        setTimeout(() => {
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 500);
    };

    try {
        const win = iframe.contentWindow;
        const doc = win.document;
        doc.open();
        doc.write(html);
        doc.close();

        win.addEventListener('afterprint', cleanup);
        // 兜底：afterprint 在部分浏览器（如某些 Safari 版本）不触发
        setTimeout(cleanup, 60000);

        // 等一帧让 iframe 完成布局/字体应用后再弹打印框
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                cleanup();
            }
        }, 150);
    } catch (e) {
        cleanup();
    }
};

export default InvoicePrintDocument;
